import { Tag } from 'domelementtype';
import { isText } from 'domhandler';
import TmlResult from '../tml/TmlResult';
const BREAKPOINTS = ['A', 'B', 'C', 'D'];
// Legacy character count constants used by the adContainers plugin
export const legacyCharacterCount = {
    embedCharacterEquivalency: {
        A: 200,
        B: 200,
        C: 200,
        D: 200,
    },
    finalAdEmbedCharacterEquivalency: {
        A: 750,
        B: 750,
        C: 750,
        D: 750,
    },
    lineBreakEquivalency: {
        A: 50,
        B: 50,
        C: 50,
        D: 50,
    },
    EMBED_CARD_MARGIN: {
        A: 200,
        B: 200,
        C: 200,
        D: 200,
    },
    ESTIMATED_LINE_LENGTH: {
        A: 45,
        B: 75,
        C: 80,
        D: 80,
    },
    CHARACTER_COUNT_SCALE: {
        h1: 2,
        h2: 2,
        h3: 1.8,
        h4: 1.6,
        h5: 1.4,
        h6: 1.2,
        default: 1,
    },
};
// TODO: Use the site style to determine the character count
// based on the actual font size and line height assigned to the element
// Header: headline-detail
// H1 A:S, B:M
// H2 A:XS, B:S
// H3 A:XXS, B:XS
// H4 A:XXXS, B:XXS
// Paragraph & Body content: body-primary
// S on all sizes
// https://codepen.io/robiosahan/pen/rNqKdJj
// Based on 16px font size
// 1rem = 16px on Phoenix
// Average line-height of 1.4em = 22.4px height
// line length is in ch units, the size of the '0' glyph for the given font.
export const ESTIMATED_LINE_LENGTH = {
    A: 52,
    B: 78,
    C: 88,
    D: 88,
};
export const CHARACTER_HEIGHT_SCALE = {
    h1: 2,
    h2: 2,
    h3: 1.8,
    h4: 1.6,
    h5: 1.4,
    h6: 1.2,
    default: 1,
};
export const defaulExternalEmbedCharCount = {
    A: 10 * ESTIMATED_LINE_LENGTH.A,
    B: 10 * ESTIMATED_LINE_LENGTH.B,
    C: 10 * ESTIMATED_LINE_LENGTH.C,
    D: 10 * ESTIMATED_LINE_LENGTH.D,
};
export const nonTxtEleCharEqui = {
    image: {
        A: 10 * ESTIMATED_LINE_LENGTH.A,
        B: 10 * ESTIMATED_LINE_LENGTH.B,
        C: 10 * ESTIMATED_LINE_LENGTH.C,
        D: 10 * ESTIMATED_LINE_LENGTH.D,
    },
    externalEmbed: {
        default: defaulExternalEmbedCharCount,
        maven(source) {
            const embedCharCount = {
                'mvn://numbrix': {
                    A: 250,
                    B: 250,
                    C: 250,
                    D: 250,
                },
            };
            const keys = Object.keys(embedCharCount);
            const key = keys.find((k) => source.startsWith(k));
            return typeof key === 'string' ? embedCharCount[key] : defaulExternalEmbedCharCount;
        },
    },
};
const WORDPRESS_BLOCK_CHAR_COUNTS = {
    'wp-block-embed': 200,
    'wp-block-image': 200,
};
const WORDPRESS_BLOCK_WORD_COUNTS = {
    'wp-block-embed': 100,
    'wp-block-image': 100,
};
const isCheerioRootElement = (el) => typeof el.children === 'function';
export const CONTENT_SIZES = {
    short: 0,
    medium: 1000,
    long: 2000,
    'x-long': 3000,
};
const getContentSize = (contentHeight = 0) => {
    const contentSizes = Object.entries(CONTENT_SIZES);
    const [size] = contentSizes.reverse().find(([_, minContentHeight]) => contentHeight >= minContentHeight) ||
        contentSizes[0];
    return size;
};
const flattenCharacterCounts = ({ contentHeight, isShallowContent, }) => {
    return {
        size: {
            A: getContentSize(contentHeight.A),
            B: getContentSize(contentHeight.B),
            C: getContentSize(contentHeight.C),
            D: getContentSize(contentHeight.D),
        },
        contentHeight,
        isShallowContent,
    };
};
const wrapIfNonEmpty = (tag) => (content) => typeof content === 'string' && content.length ? `<${tag}>${content}</${tag}>` : '';
const INITIAL_VIEWPORT_CHARACTER_COUNT = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
};
export class ContentStats {
    constructor({ item, shallowConfig, featureFlagChecker }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.nodes = [];
        this.tmlNodes = [];
        this.slots = {
            A: [],
            B: [],
            C: [],
            D: [],
        };
        this._contentHeight = Object.assign({}, INITIAL_VIEWPORT_CHARACTER_COUNT);
        this._characterCount = 0;
        this._rawCharacterCount = 0;
        this._wordCount = 0;
        this._rawWordCount = 0;
        this._hasSlideshowGallery = false;
        this.featureFlagChecker = featureFlagChecker;
        this.shallowConfig = Object.assign({}, { enabled: false, threshold: 0 }, shallowConfig);
        this.item = item;
        // Process Tml properties
        this.createNodes();
        // Process Tml Embeds and Images
        this.addExternalEmbedsCharHeight();
        this.addImageCharHeight();
        this.addCollectionCharHeight();
        this.addListCharHeight();
    }
    get characterCountDefaults() {
        const { embedCharacterEquivalency, EMBED_CARD_MARGIN, finalAdEmbedCharacterEquivalency } = legacyCharacterCount;
        return this.featureFlagChecker.isEnabled('use-content-stat-ads-constants')
            ? {
                embedCharacterEquivalency,
                finalAdEmbedCharacterEquivalency,
                lineBreakEquivalency: ESTIMATED_LINE_LENGTH,
                EMBED_CARD_MARGIN,
                ESTIMATED_LINE_LENGTH,
                CHARACTER_COUNT_SCALE: CHARACTER_HEIGHT_SCALE,
            }
            : legacyCharacterCount;
    }
    get tmlProps() {
        return Object.keys(this.item).filter((key) => key.endsWith('Tml'));
    }
    get objectType() {
        return this.item.objectType;
    }
    get extension() {
        return this.item.extension;
    }
    get tmlEmbeds() {
        return this.item.tmlEmbeds || [];
    }
    get contentHeight() {
        return this._contentHeight;
    }
    // Add value's heights to each breakpoint.
    set contentHeight(value) {
        BREAKPOINTS.forEach((key) => {
            const val = value[key] || 0;
            const currentVal = this.contentHeight[key] || 0;
            this._contentHeight[key] = currentVal + val;
        });
    }
    get characterCount() {
        return this._characterCount;
    }
    get rawCharacterCount() {
        return this._rawCharacterCount;
    }
    get wordCount() {
        return this._wordCount;
    }
    get rawWordCount() {
        return this._rawWordCount;
    }
    get hasSlideshowGallery() {
        return this._hasSlideshowGallery;
    }
    imageCount() {
        const images = this.tmlNodes.filter((obj) => !!obj?.attributes?.['tml-image']);
        return images.length;
    }
    getAttributeValues(attribute, node) {
        return node?.attributes?.[attribute];
    }
    getByAttribute(attribute) {
        return this.tmlNodes.filter((obj) => !!obj?.attributes?.[attribute]);
    }
    createNodes() {
        if (!this.tmlProps.length) {
            return;
        }
        this.tmlProps.forEach((tml) => {
            if (this.item[tml] !== undefined) {
                this.processNodes(this.item[tml]);
            }
        });
    }
    contentMeta() {
        return flattenCharacterCounts({
            contentHeight: this.contentHeight,
            isShallowContent: this.isShallowContent(),
        });
    }
    extractNodeData(nodes) {
        return nodes.map((child) => ({
            type: child.type,
            name: child.type === Tag ? child.name : '',
            contentHeight: this.getContentHeight(child),
            attributes: child.type === Tag ? child.attribs : {},
            children: child.type === Tag && child.children?.length > 0 ? this.extractNodeData(child.children) : [],
        }));
    }
    getContentHeight(element) {
        let contentHeight = Object.assign({}, INITIAL_VIEWPORT_CHARACTER_COUNT);
        /*
            // Check for the max length of the text and use the default length if smaller than the max
            h1 // We don't add it in the TML content
            h2,
            h3,
            h4,
            h5,
            h6,
            -----
            li,
            p,
            aside,
            blockquote,
            br
            ------
            div // are used to add block level TML content
        */
        // Direct children of the root element in the TML content
        // "li" is not the direct child but we want to compute the
        // character count of the text inside it and not the text inside the "ul"
        if (element.type === Tag) {
            const tagNames = [
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'li',
                'p',
                'strong',
                'em',
                'aside',
                'blockquote',
                'figcaption',
                'a',
                'cite',
                'div',
                'span',
            ];
            if (tagNames.includes(element.name)) {
                let text = element.children.reduce((l, c) => l + ((isText(c) ? c.data.replace(/[ ]+/g, ' ') : '') ?? ''), '');
                // trim all leading space for this element
                text = text.replace(/^\s+/, '');
                // reduce all trailing space to a single space
                text = text.replace(/\s+$/, ' ');
                // if element only contains spaces, don't count it
                if (text.replace(/\n+/g, '').trim() === '') {
                    text = '';
                }
                // don't count newlines as characters
                const chars = text.replace(/\n+/g, '').length;
                this._characterCount += chars;
                this._rawCharacterCount += chars;
                // Exclude spans that contain non-word characters (punctuation, etc) from word count.
                const words = text.length ? text.split(/\s+/).filter((s) => !!s.match(/\w/)).length : 0;
                this._wordCount += words;
                this._rawWordCount += words;
                const scale = CHARACTER_HEIGHT_SCALE[element.name] || CHARACTER_HEIGHT_SCALE.default;
                contentHeight = {
                    A: Math.max(ESTIMATED_LINE_LENGTH.A, chars * scale),
                    B: Math.max(ESTIMATED_LINE_LENGTH.B, chars * scale),
                    C: Math.max(ESTIMATED_LINE_LENGTH.C, chars * scale),
                    D: Math.max(ESTIMATED_LINE_LENGTH.D, chars * scale),
                };
            }
            if (element.name === 'figure') {
                const [wpBlockType] = /wp-block-(image|embed)\b/.exec(element.attribs?.class) ?? [];
                if (wpBlockType != null) {
                    const _len = WORDPRESS_BLOCK_CHAR_COUNTS[wpBlockType] ?? 0;
                    this._characterCount += _len;
                    contentHeight = {
                        A: _len * CHARACTER_HEIGHT_SCALE.default,
                        B: _len * CHARACTER_HEIGHT_SCALE.default,
                        C: _len * CHARACTER_HEIGHT_SCALE.default,
                        D: _len * CHARACTER_HEIGHT_SCALE.default,
                    };
                    const _words = WORDPRESS_BLOCK_WORD_COUNTS[wpBlockType] ?? 0;
                    this._wordCount += _words;
                }
            }
        }
        this.contentHeight = contentHeight;
        return contentHeight;
    }
    processNodes(tml) {
        const tmlResult = new TmlResult(tml.replace(/<br>/gi, '\n'), this.tmlEmbeds);
        const parsedHtml = tmlResult.parsedHtml;
        const ele = parsedHtml.root().toArray()[0];
        const children = isCheerioRootElement(ele) ? ele.children() : ele.children;
        this.tmlNodes.push(...(children?.length ? this.extractNodeData(children) : []));
    }
    // Add the text height equivalent for all tml-collections
    addCollectionCharHeight() {
        this.getByAttribute('tml-collection').forEach((galleryEle) => {
            const galleryId = this.getAttributeValues('tml-collection', galleryEle);
            let gallery = this.item.tmlEmbeds?.find(({ id }) => id === galleryId);
            if (!gallery) {
                return;
            }
            gallery = { ...gallery };
            gallery.tmlEmbeds = this.tmlEmbeds.filter(({ id }) => id !== galleryId);
            const collectionDetails = new ContentGalleryDetails({
                item: gallery,
                element: galleryEle,
                shallowConfig: this.shallowConfig,
                featureFlagChecker: this.featureFlagChecker,
            });
            this.contentHeight = collectionDetails.contentHeight;
            this._characterCount = collectionDetails._characterCount;
            this._wordCount = collectionDetails._wordCount;
            this._hasSlideshowGallery || (this._hasSlideshowGallery = collectionDetails.isSlideshowGallery);
        });
    }
    // Add the text height equivalent for all tml-list
    addListCharHeight() {
        this.getByAttribute('tml-list').forEach((element) => {
            const listId = this.getAttributeValues('tml-list', element);
            let list = this.item.tmlEmbeds?.find(({ id }) => id === listId);
            if (!list) {
                return;
            }
            list = { ...list };
            list.tmlEmbeds = this.tmlEmbeds.filter(({ id }) => id !== listId);
            const listDetails = new ContentListDetails({
                item: list,
                shallowConfig: this.shallowConfig,
                featureFlagChecker: this.featureFlagChecker,
            });
            this.contentHeight = listDetails.contentHeight;
            this._characterCount = listDetails._characterCount;
            this._wordCount = listDetails._wordCount;
        });
    }
    // Add the text height equivalent for all external embeds
    addExternalEmbedsCharHeight() {
        const externalEmbeds = this.getByAttribute('tml-external-provider');
        const grouped = externalEmbeds.reduce((acc, ele) => {
            const provider = this.getAttributeValues('tml-external-provider', ele);
            const providerEle = typeof provider === 'string' ? acc[provider] ?? [] : [];
            providerEle.push(ele);
            if (typeof provider === 'string') {
                acc[provider] = providerEle;
            }
            return acc;
        }, {});
        Object.entries(grouped).forEach(([provider, elements]) => {
            const values = nonTxtEleCharEqui.externalEmbed;
            const contentHeights = values[provider] !== undefined ? values[provider] : values.default;
            if (typeof contentHeights === 'function') {
                elements.forEach((element) => {
                    const src = this.getAttributeValues('tml-embed-src', element);
                    this.contentHeight = contentHeights(src);
                });
            }
            else {
                const { A, B, C, D } = contentHeights;
                this.contentHeight = {
                    A: elements.length * A,
                    B: elements.length * B,
                    C: elements.length * C,
                    D: elements.length * D,
                };
            }
        });
    }
    // Add the text height equivalent for all images
    addImageCharHeight() {
        const images = this.imageCount();
        const imageTxtEleCharEqui = nonTxtEleCharEqui.image;
        this.contentHeight = {
            A: images * imageTxtEleCharEqui.A,
            B: images * imageTxtEleCharEqui.B,
            C: images * imageTxtEleCharEqui.C,
            D: images * imageTxtEleCharEqui.D,
        };
    }
    // Starting in October 2020, Google began flagging pages using AdManager or
    // AdX that "do not contain content"--things like running an article made up
    // of one or two Instagram embeds. This is an optional check to make sure that
    // the word count is above a configurable threshold. We treat certain page
    // types--e.g. stock quotes--as presumptively shallow, and then ignore items
    // that do not have bodyTml. Pages which are not automatically generated but
    // maintained as static pages should be demonetized at the page template level.
    //
    // We will treat photos, hub pages, and rich terms as presumptively "deep" for
    // the time being. Video pages will be demonetized if they don't have
    // a sufficient amount of introductory content. These behaviors are subject to
    // change based on further discovery about what signals Google is using to
    // detect pages they think are abusive.
    isShallowContent() {
        const { enabled, threshold } = this.shallowConfig;
        if (!enabled) {
            return false;
        }
        const safeTypes = ['ContentHubPage', 'ContentPhoto', 'ContentRichTerm'];
        if (safeTypes.includes(this.objectType)) {
            return false;
        }
        // Some pages are presumptively shallow:
        // * Stock ticker pages
        // * ContentUserProfiles (author pages)
        // * Team pages
        // * Player pages
        // Game pages will be treated as standard articles; if there's enough
        // content in the bodyTml, we should not treat them as shallow.
        if (this.objectType === 'ContentUserProfile' ||
            this.objectType === 'ContentPlayer' ||
            (this.objectType === 'ContentOrganization' && this.extension === 'ticker')) {
            return true;
        }
        return this.contentHeight.C < threshold;
    }
}
class ContentGalleryDetails extends ContentStats {
    constructor(deps) {
        super(deps);
        this.element = deps.element;
        this.item = deps.item;
        this.processGalleryNodes();
    }
    get renderStyle() {
        // slideshow|longform|flat|diptych|triptych
        return this.getAttributeValues('tml-render-style', this.element);
    }
    get hasFullInlineContent() {
        // Only "flat" gallery renders as a list of images and content
        return this.renderStyle === 'flat';
    }
    get isSlideshowGallery() {
        return this.renderStyle === 'slideshow';
    }
    imageCount() {
        // For all other render styles, we only render CTA (mostly equivalent to one landscape image)
        return this.hasFullInlineContent ? this.item.itemIds.length : 1;
    }
    processGalleryNodes() {
        if (!this.hasFullInlineContent) {
            return;
        }
        // This implementation is based on the assumption that the gallery is the flat gallery
        const { itemsAttribution = {}, itemIds = [], title = '', teaser = '' } = this.item;
        const itemTitleTag = title?.length ? 'h3' : 'h2';
        const slides = itemIds
            .map((id) => {
            return itemsAttribution[id] === undefined ? {} : itemsAttribution[id];
        })
            .map(({ captionTml = '', title = '' }) => `${wrapIfNonEmpty(itemTitleTag)(title)}${captionTml}`)
            .join('');
        this.processNodes(`${wrapIfNonEmpty('h2')(title)}${wrapIfNonEmpty('p')(teaser)}${slides}`);
    }
}
class ContentListDetails extends ContentStats {
    constructor(deps) {
        super(deps);
        this.item = deps.item;
        this.processRichListNodes();
    }
    processRichListNodes() {
        const { items = [], formattedTitleTmlInline = '', teaser = '' } = this.item;
        const itemTitleTag = formattedTitleTmlInline?.length ? 'h3' : 'h2';
        const slides = items
            .map(({ descriptionTml = '', formattedTitleTmlInline = '' }) => {
            return `${wrapIfNonEmpty(itemTitleTag)(formattedTitleTmlInline)}${descriptionTml}`;
        })
            .join('');
        this.processNodes(`${wrapIfNonEmpty('h2')(formattedTitleTmlInline)}${wrapIfNonEmpty('p')(teaser)}${slides}`);
    }
}
//# sourceMappingURL=ContentStats.js.map